/**
 * Segment card used inside the timeline track and replace-panel.
 * Purpose-coloured stripe makes the cut structure visually scannable.
 */
import { GripVertical } from 'lucide-react'

import type { TimelinePurpose } from '@/types/drama'
import { Badge } from '@/components/ui/badge'

interface Props {
  segmentCode: string
  episodeNumber?: number
  purpose: TimelinePurpose
  caption?: string | null
  durationLabel?: string
  active?: boolean
  draggable?: boolean
  onClick?: () => void
  onDragStart?: (e: React.DragEvent) => void
  onDragOver?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent) => void
}

// [SOP-2026-06-04] Editor SOP long-form purpose styling (§6.5.1).
export const PURPOSE_STYLE: Record<TimelinePurpose, { tint: string; label: string }> = {
  opening_highlight: { tint: 'bg-amber-500/15 border-amber-500', label: '开场爆点' },
  voiceover_setup: { tint: 'bg-sky-500/15 border-sky-500', label: '旁白铺垫' },
  reverse_context: { tint: 'bg-slate-500/15 border-slate-500', label: '倒叙背景' },
  plot_progression: { tint: 'bg-blue-500/15 border-blue-500', label: '剧情推进' },
  emotional_peak: { tint: 'bg-rose-500/15 border-rose-500', label: '情绪高潮' },
  final_highlight: { tint: 'bg-fuchsia-500/15 border-fuchsia-500', label: '结尾爆点' },
  tail_card: { tint: 'bg-violet-500/15 border-violet-500', label: '片尾卡' },
}

export function SegmentCard({
  segmentCode,
  episodeNumber,
  purpose,
  caption,
  durationLabel,
  active = false,
  draggable = false,
  onClick,
  onDragStart,
  onDragOver,
  onDrop,
}: Props) {
  const style = PURPOSE_STYLE[purpose] || { tint: 'bg-muted', label: purpose }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={[
        'group relative w-32 flex-shrink-0 rounded-md border-2 p-2 cursor-pointer transition-all',
        style.tint,
        active ? 'ring-2 ring-spectrum-magenta ring-offset-2' : 'hover:scale-[1.02]',
      ].join(' ')}
    >
      {draggable && (
        <GripVertical className="w-3 h-3 absolute top-1.5 right-1.5 text-muted-foreground opacity-0 group-hover:opacity-70" />
      )}
      <Badge variant="outline" className="text-[10px] mb-1.5">
        {style.label}
      </Badge>
      <div className="text-xs font-mono leading-none">{segmentCode}</div>
      {episodeNumber != null && (
        <div className="text-[10px] text-muted-foreground mt-1">EP {episodeNumber}</div>
      )}
      {durationLabel && (
        <div className="text-[10px] text-muted-foreground">{durationLabel}</div>
      )}
      {caption && (
        <div className="text-[10px] mt-1.5 line-clamp-2 italic text-muted-foreground">
          "{caption}"
        </div>
      )}
    </div>
  )
}
